import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Users, TrendingUp, LogOut, Download, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { fetchVideoData, fetchAllVideos } from "@/services/youtube";
import { StatCard } from "@/components/dashboard/StatCard";
import { CommentTimeline } from "@/components/dashboard/CommentTimeline";
import { TrendingKeywords } from "@/components/dashboard/TrendingKeywords";
import { ActiveCommenters } from "@/components/dashboard/ActiveCommenters";
import { TrendingTopics } from "@/components/dashboard/TrendingTopics";
import { useQuery } from "@tanstack/react-query";
import { KeywordTrend, ActiveCommenter, Comment, TrendingTopic, TopicMention, TimelineData } from "@/types/supabase";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Filler,
  Legend
} from 'chart.js';
import { 
  downloadCSV, 
  prepareKeywordTrendsForCSV, 
  prepareActiveCommentersForCSV, 
  prepareTrendingTopicsForCSV 
} from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Filler,
  Legend
);

const Index = () => {
  const [selectedVideo, setSelectedVideo] = useState("all");
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const loadVideos = async () => {
    try {
      const data = await fetchAllVideos();
      setVideos(data || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load videos",
      });
    }
  };

  const handleAddVideo = async () => {
    try {
      setLoading(true);
      
      // Validate YouTube URL format
      if (!videoUrl.includes('youtube.com/watch?v=') && !videoUrl.includes('youtu.be/')) {
        throw new Error("Please enter a valid YouTube URL");
      }
      
      // Extract video ID from URL (support both youtube.com and youtu.be formats)
      let videoId = '';
      if (videoUrl.includes('youtube.com/watch?v=')) {
        videoId = videoUrl.split('v=')[1]?.split('&')[0];
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
      }
      
      if (!videoId) {
        throw new Error("Could not extract video ID from URL");
      }

      const videoData = await fetchVideoData(videoId);
      await loadVideos();
      setVideoUrl("");
      
      // Check if there was an analysis error but the video was still added
      if (videoData.analysisError) {
        toast({
          variant: "warning",
          title: "Video Added with Warnings",
          description: `Video added, but comment analysis failed: ${videoData.analysisError}`,
        });
      } else {
        toast({
          title: "Success",
          description: "Video added successfully",
        });
      }
    } catch (error: any) {
      console.error("Error adding video:", error);
      
      // Display user-friendly error message based on error type
      let errorMessage = error.message || "An unknown error occurred";
      
      // Make common error messages more user-friendly
      if (errorMessage.includes("Video not found")) {
        errorMessage = "Video not found. Please check the URL and try again.";
      } else if (errorMessage.includes("API key")) {
        errorMessage = "YouTube API authorization failed. Please contact the administrator.";
      } else if (errorMessage.includes("quota")) {
        errorMessage = "YouTube API quota exceeded. Please try again later.";
      } else if (errorMessage.includes("Comments disabled")) {
        errorMessage = "This video has comments disabled. Some analytics features may not work.";
      } else if (errorMessage.includes("function error")) {
        errorMessage = "Server error processing the video. Please try again later.";
      }
      
      toast({
        variant: "destructive",
        title: "Error Adding Video",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedVideoData = () => {
    console.log('Selected video:', selectedVideo);
    console.log('Available videos:', videos);
    if (selectedVideo === "all") {
      return videos;
    }
    if (selectedVideo === "latest") {
      // Return the latest video with its actual ID
      return videos.length > 0 ? [videos[0]] : [];
    }
    const filtered = videos.filter(video => String(video.id) === String(selectedVideo));
    console.log('Filtered videos:', filtered);
    return filtered;
  };

  const { data: keywordTrends, isLoading: isLoadingKeywords } = useQuery({
    queryKey: ['keywordTrends', selectedVideo],
    queryFn: async () => {
      console.log('Fetching keyword trends for video:', selectedVideo);
      const { data, error } = await supabase
        .rpc<KeywordTrend[]>('get_keyword_trends_by_video', {
          video_filter: selectedVideo === 'all' ? null : 
                        (selectedVideo === 'latest' && videos.length > 0) ? String(videos[0].id) : selectedVideo
        });

      if (error) {
        console.error('Error fetching keyword trends:', error);
        throw error;
      }
      
      console.log('Keyword trends data:', data);
      
      if (!data || data.length === 0) {
        return [];
      }

      // Find the maximum occurrence count for scaling
      const maxCount = Math.max(...data.map(trend => trend.occurrence_count));
      const minCount = Math.min(...data.map(trend => trend.occurrence_count));
      const range = maxCount - minCount || 1; // Prevent division by zero
      
      // Calculate sizes with a more dramatic range (24-64 instead of 16-32)
      return data.map(trend => ({
        text: trend.keyword,
        size: Math.round(
          24 + // minimum size
          ((trend.occurrence_count - minCount) / range) * 40 // scale up to 40 more pixels
        ),
        count: trend.occurrence_count
      }));
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const { data: activeCommentersData, isLoading: isLoadingCommenters } = useQuery({
    queryKey: ['activeCommenters', selectedVideo],
    queryFn: async () => {
      console.log('Fetching active commenters for video:', selectedVideo);
      // First get active commenters
      const { data: commenters, error: commentersError } = await supabase
        .rpc<ActiveCommenter[]>('get_active_commenters_by_video', {
          video_filter: selectedVideo === 'all' ? null : 
                        (selectedVideo === 'latest' && videos.length > 0) ? String(videos[0].id) : selectedVideo
        });

      if (commentersError) {
        console.error('Error fetching active commenters:', commentersError);
        throw commentersError;
      }

      if (!commenters || commenters.length === 0) {
        return [];
      }

      // Then get their recent comments
      const commenterPromises = commenters.map(async (commenter) => {
        const { data: comments, error: commentsError } = await supabase
          .rpc<Comment[]>('get_comments_by_author_and_video', {
            author_name: commenter.author,
            video_filter: selectedVideo === 'all' ? null : 
                          (selectedVideo === 'latest' && videos.length > 0) ? String(videos[0].id) : selectedVideo
          });

        if (commentsError) {
          console.error('Error fetching comments:', commentsError);
          return {
            name: commenter.author,
            comments: commenter.comment_count,
            recentComments: []
          };
        }

        return {
          name: commenter.author,
          comments: commenter.comment_count,
          recentComments: comments && comments.length > 0 ? comments.map(comment => ({
            text: comment.text.replace(/<[^>]*>/g, ''), // Remove HTML tags
            created_at: comment.published_at || comment.created_at || '' // Use published_at if available, fall back to created_at
          })) : []
        };
      });

      const results = await Promise.all(commenterPromises);
      console.log('Active commenters with comments for video:', selectedVideo, results);
      return results;
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const { data: trendingTopicsData, isLoading: isLoadingTopics } = useQuery({
    queryKey: ['trendingTopics', selectedVideo],
    queryFn: async () => {
      console.log('Fetching trending topics for video:', selectedVideo);
      const { data: topics, error: topicsError } = await supabase
        .rpc<TrendingTopic[]>('get_trending_topics_by_video', {
          video_filter: selectedVideo === 'all' ? null : 
                        (selectedVideo === 'latest' && videos.length > 0) ? String(videos[0].id) : selectedVideo
        });

      if (topicsError) {
        console.error('Error fetching trending topics:', topicsError);
        throw topicsError;
      }

      // For each topic, get the mentions
      const topicsWithMentions = await Promise.all(
        (topics || []).map(async (topic) => {
          const { data: mentions, error: mentionsError } = await supabase
            .rpc<TopicMention[]>('get_topic_mentions_by_video', {
              topic_name: topic.topic,
              video_filter: selectedVideo === 'all' ? null : 
                           (selectedVideo === 'latest' && videos.length > 0) ? String(videos[0].id) : selectedVideo
            });

          if (mentionsError) {
            console.error('Error fetching topic mentions:', mentionsError);
            return {
              topic: topic.topic,
              mentions: topic.mention_count,
              details: []
            };
          }

          return {
            topic: topic.topic,
            mentions: topic.mention_count,
            details: mentions || []
          };
        })
      );

      return topicsWithMentions;
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const selectedData = getSelectedVideoData();
  console.log('Selected video data for comments:', selectedData.map(v => ({
    id: v.id,
    title: v.title,
    commentCount: v.commentCount
  })));

  // Calculate total comments directly from the selected videos
  const totalComments = selectedData.reduce((sum, video) => {
    console.log('Video data:', {
      id: video.id,
      title: video.title,
      commentCount: video.commentCount,
      rawCommentCount: video.comment_count // Check if this exists
    });
    return sum + (video.commentCount || video.comment_count || 0);
  }, 0);
  console.log('Total comments calculated:', totalComments);

  const timeData = selectedData.length ? selectedData.map(video => {
    try {
      if (!video.published_at) {
        return null;
      }
      const date = new Date(video.published_at);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', video.published_at);
        return null;
      }
      return {
        date: date.toISOString(),
        comments: video.commentCount || 0,
      };
    } catch (error) {
      console.warn('Error parsing date:', error);
      return null;
    }
  }).filter(Boolean) : [];

  /**
   * Handles downloading all data as CSV
   */
  const handleDownloadCSV = async (dataType?: string) => {
    try {
      setIsDownloading(true);
      
      // Generate a timestamp for the filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const videoLabel = selectedVideo === 'all' ? 'all-videos' : 
                         selectedVideo === 'latest' ? 'latest-video' : 
                         `video-${selectedVideo}`;
      
      // If downloading timeline data
      if (!dataType || dataType === 'timeline') {
        // Get timeline data
        const { data: timelineData, error: timelineError } = await supabase
          .rpc<TimelineData[]>('get_comment_timeline', {
            video_filter: selectedVideo === 'all' ? null : 
                          (selectedVideo === 'latest' && videos.length > 0) ? String(videos[0].id) : selectedVideo
          });
        
        if (timelineError) {
          throw timelineError;
        }
        
        // Prepare timeline data for CSV
        const timelineCSVData = (timelineData || []).map(item => ({
          'Date': new Date(item.time_bucket).toISOString().split('T')[0],
          'Comment Count': item.comment_count
        }));
        
        if (timelineCSVData.length > 0) {
          downloadCSV(
            timelineCSVData, 
            `chatter-vibe-timeline-data-${videoLabel}-${timestamp}.csv`
          );
        }
      }
      
      // If downloading keyword trends
      if (!dataType || dataType === 'keywords') {
        const keywordTrendsCSV = prepareKeywordTrendsForCSV(keywordTrends || []);
        if (keywordTrendsCSV.length > 0) {
          downloadCSV(
            keywordTrendsCSV, 
            `chatter-vibe-keyword-trends-${videoLabel}-${timestamp}.csv`
          );
        }
      }
      
      // If downloading active commenters
      if (!dataType || dataType === 'commenters') {
        const activeCommentersCSV = prepareActiveCommentersForCSV(activeCommentersData || []);
        if (activeCommentersCSV.length > 0) {
          downloadCSV(
            activeCommentersCSV, 
            `chatter-vibe-active-commenters-${videoLabel}-${timestamp}.csv`
          );
        }
      }
      
      // If downloading trending topics
      if (!dataType || dataType === 'topics') {
        const trendingTopicsCSV = prepareTrendingTopicsForCSV(trendingTopicsData || []);
        if (trendingTopicsCSV.length > 0) {
          downloadCSV(
            trendingTopicsCSV, 
            `chatter-vibe-trending-topics-${videoLabel}-${timestamp}.csv`
          );
        }
      }
      
      toast({
        title: "Success",
        description: dataType ? `${dataType.charAt(0).toUpperCase() + dataType.slice(1)} data has been downloaded` : "CSV reports have been downloaded",
      });
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to download CSV: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-zinc-950 text-white p-6">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">Comments Analytics</h1>
          <div className="flex items-center gap-4">
            <Select 
              value={selectedVideo}
              onValueChange={setSelectedVideo}
              defaultValue="all"
            >
              <SelectTrigger className="w-[280px] bg-zinc-800/50 border-zinc-700 text-white">
                <SelectValue placeholder="Select a video" />
              </SelectTrigger>
              <SelectContent>
                {[
                  { id: 'static_all', title: 'All Videos', value: 'all' },
                  { id: 'static_latest', title: 'Latest Upload', value: 'latest' },
                  ...videos
                    .filter(video => video && video.id) // Filter out videos with undefined IDs
                    .map(video => ({
                      id: `video_${String(video.id)}`,
                      title: video.title || 'Untitled Video',
                      value: String(video.id)
                    }))
                ].map(item => (
                  <SelectItem 
                    key={item.id}
                    value={item.value}
                  >
                    {item.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={isDownloading}
                  className="text-zinc-200 bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700 hover:text-white"
                >
                  {isDownloading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download CSV
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-800 border-zinc-700 text-white">
                <DropdownMenuItem 
                  onClick={() => handleDownloadCSV()}
                  className="cursor-pointer hover:bg-zinc-700"
                >
                  All Data
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDownloadCSV('timeline')}
                  className="cursor-pointer hover:bg-zinc-700"
                >
                  Timeline Data
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDownloadCSV('keywords')}
                  className="cursor-pointer hover:bg-zinc-700"
                >
                  Keyword Trends
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDownloadCSV('commenters')}
                  className="cursor-pointer hover:bg-zinc-700"
                >
                  Active Commenters
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDownloadCSV('topics')}
                  className="cursor-pointer hover:bg-zinc-700"
                >
                  Trending Topics
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button
              variant="ghost"
              onClick={handleSignOut}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800/50"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex gap-4 items-center">
          <Input
            type="text"
            placeholder="Enter YouTube video URL"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            className="flex-1 bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-400"
          />
          <Button
            onClick={handleAddVideo}
            disabled={loading}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {loading ? "Adding..." : "Add Video"}
          </Button>
        </div>

        <div className="flex items-center gap-3 py-4">
          <h2 className="text-5xl font-bold">
            {selectedVideo === "all" ? "All Videos" :
             selectedVideo === "latest" ? "Latest Upload" :
             videos.find(v => String(v.id) === selectedVideo)?.title || "Selected Video"}
          </h2>
          {selectedVideo !== "all" && (
            selectedVideo === "latest" && videos.length === 0 ? null : (
              <a
                href={`https://www.youtube.com/watch?v=${selectedVideo === "latest" ? videos[0]?.id : selectedVideo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-400 hover:text-violet-300 text-base"
              >
                View on YouTube
              </a>
            )
          )}
        </div>

        <div className="grid grid-cols-4 gap-4">
          <StatCard
            title="Total Comments"
            icon={<MessageSquare className="w-4 h-4" />}
            value={totalComments.toString()}
            trend="0%"
          />
          <StatCard
            title="Active Commenters"
            icon={<Users className="w-4 h-4" />}
            value={(activeCommentersData?.length || 0).toString()}
            trend="0%"
          />
          <StatCard
            title="Trending Topics"
            icon={<TrendingUp className="w-4 h-4" />}
            value={(trendingTopicsData?.length || 0).toString()}
            trend="0%"
          />
        </div>

        <div className="grid grid-cols-4 gap-4">
          <CommentTimeline 
            selectedVideo={
              selectedVideo === "latest" 
                ? (videos.length > 0 ? String(videos[0].id) : "none") 
                : selectedVideo
            } 
          />
          
          <div className="col-span-4 bg-zinc-800/20 border border-zinc-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Trending Keywords</h3>
            <TrendingKeywords 
              keywords={keywordTrends || []} 
            />
          </div>
          
          <div className="col-span-2 h-[400px] overflow-y-auto">
            <ActiveCommenters 
              commenters={activeCommentersData || []} 
            />
          </div>
          <div className="col-span-2 h-[400px] overflow-y-auto">
            <TrendingTopics 
              topics={trendingTopicsData || []} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
